import { Renderer2 } from '@angular/core';
import { OpenModalButtonIds } from '../../interface/checkin-seatmap/open-modal-button-ids';
import { SeatLegendDisplayInformation } from '../../interface/checkin-seatmap/seat-legend-display-information';
import { isPC, isSP, isTB } from '@lib/helpers';
import { AppConstants } from '@conf/app.constants';

/**
 * キャビンコードからキャビンの名称を取得する。
 * @param cabinCode キャビンコード
 * @returns キャビンの名称
 */
export const getCabinNameByCabinCode = (cabinCode: string) => {
  switch (cabinCode) {
    // エコノミー
    case 'Y':
      return 'eco';

    // プレミアムエコノミー
    case 'E':
      return 'ecoPremium';

    // ビジネス
    case 'C':
      return 'business';

    // ファースト
    case 'F':
      return 'first';

    default:
      return '';
  }
};

/**
 * 表示すべき凡例の情報を格納したリストを作成する
 * @param isContainedRearFacingSeat
 * @param isEconomy キャビンがエコノミークラスかどうか
 */
export function createLegendList(isContainedRearFacingSeat: boolean, isEconomy: boolean) {
  const legendList: SeatLegendDisplayInformation[] = [];
  const seatLegendSuffix = isEconomy ? '24' : 'armrest_24';

  /** 後ろ向き席を含む場合、 */
  if (isContainedRearFacingSeat) {
    // 前向き席の追加
    legendList.push({
      staticMessageKey: 'm_static_message-label.forwardFacingSeat',
      imageUrl: `assets/images/seat_front_seat_${seatLegendSuffix}.svg`,
    });
    // 後ろ向き席の追加の追加
    legendList.push({
      staticMessageKey: 'm_static_message-label.backSeat',
      imageUrl: `assets/images/seat_back_seat_${seatLegendSuffix}.svg`,
    });
  } else {
    // 選択可能席の追加
    legendList.push({
      staticMessageKey: 'm_static_message-label.freeSeat',
      imageUrl: `assets/images/seat_free_seat_${seatLegendSuffix}.svg`,
    });
  }
  // 非常口席
  legendList.push({
    staticMessageKey: 'm_static_message-label.exitSeat',
    imageUrl: AppConstants.IMG.C01_P040_S01_U_LEGENDS_EMERGENCY_EXIT_SEAT,
  });
  // 幼児
  legendList.push({
    staticMessageKey: 'm_static_message-label.infant',
    imageUrl: `assets/images/seat_infant_24.svg`,
  });
  // 選択不可能席
  legendList.push({
    staticMessageKey: 'm_static_message-label.unselectable',
    imageUrl: `assets/images/seat_unavailable_24.svg`,
  });
  // 選択中席
  legendList.push({
    staticMessageKey: 'm_static_message-label.selecting',
    imageUrl: AppConstants.IMG.C01_P040_S01_U_LEGENDS_SEAT_SELECTED,
  });
  // 変更前席
  legendList.push({
    staticMessageKey: 'm_static_message-label.changeBeforeFreeSeat',
    imageUrl: AppConstants.IMG.C01_P040_S01_U_LEGENDS_SEAT_BEFORE_CHANGE_FREE_SEAT,
  });
  // 指定済み変更不可席
  legendList.push({
    staticMessageKey: 'm_static_message-label.unalterableSoSelected',
    imageUrl: `assets/images/seat_unchangeable_24.svg`,
  });
  // 窓なし席
  legendList.push({
    staticMessageKey: 'm_static_message-label.nonWindow',
    imageUrl: `assets/images/seat_no_window_24.svg`,
  });
  // リクライニング制限席
  legendList.push({
    staticMessageKey: 'm_static_message-label.impossibleReclining',
    imageUrl: `assets/images/seat_no_reclining_24.svg`,
  });
  // バジネット
  legendList.push({
    staticMessageKey: 'm_static_message-label.bassinetSeat',
    imageUrl: `assets/images/seat_bassinet_24.svg`,
  });
  // テーブル
  legendList.push({
    staticMessageKey: 'm_static_message-label.table',
    imageUrl: `assets/images/seat_table_24.svg`,
  });
  // 化粧室
  legendList.push({
    staticMessageKey: 'm_static_message-label.restroom',
    imageUrl: AppConstants.IMG.C01_P040_S01_U_LEGENDS_SEAT_LAVATORY,
  });
  // 多目的ルーム
  legendList.push({
    staticMessageKey: 'm_static_message-label.multipurposeRoom',
    imageUrl: `assets/images/seat_multi_purpose_room_24.svg`,
  });
  // 通路
  legendList.push({
    staticMessageKey: 'm_static_message-label.passage',
    imageUrl: `assets/images/seat_aisle_24.svg`,
  });
  // 非常口
  legendList.push({
    staticMessageKey: 'm_static_message-label.exit',
    imageUrl: `assets/images/seat_emergency_exit_24.svg`,
  });
  // 翼
  legendList.push({
    staticMessageKey: 'm_static_message-label.wing',
    imageUrl: AppConstants.IMG.C01_P040_S01_U_LEGENDS_WING,
  });
  // ギャレー
  legendList.push({
    staticMessageKey: 'm_static_message-label.galley',
    imageUrl: AppConstants.IMG.C01_P040_S01_U_LEGENDS_GALLEY,
  });
  // パーティション
  legendList.push({
    staticMessageKey: 'm_static_message-label.partition',
    imageUrl: `assets/images/seat_partition_24.svg`,
  });
  // バー
  legendList.push({
    staticMessageKey: 'm_static_message-label.barCounter',
    imageUrl: `assets/images/seat_bar_24.svg`,
  });
  // 階段
  legendList.push({
    staticMessageKey: 'm_static_message-label.stairs',
    imageUrl: `assets/images/seat_stair_24.svg`,
  });
  return legendList;
}

/**
 * シートマップレイアウトに表示すべき凡例の情報を格納したリストを作成する
 */
export function createLegendListForSeatmapLayout() {
  const legendList: SeatLegendDisplayInformation[] = [];
  // ギャレー
  legendList.push({
    staticMessageKey: 'm_static_message-label.galley',
    imageUrl: 'assets/images/seatlayout_galley_29.png',
  });
  // バーコーナー
  legendList.push({
    staticMessageKey: 'm_static_message-label.barCounter',
    imageUrl: 'assets/images/seatlayout_bar_29.png',
  });
  // モニター
  legendList.push({
    staticMessageKey: 'm_static_message-label.monitor',
    imageUrl: 'assets/images/seatlayout_monitor_29.png',
  });
  // 非常口
  legendList.push({
    staticMessageKey: 'm_static_message-label.exit',
    imageUrl: 'assets/images/seatlayout_emergency_exit_29.png',
  });
  // 化粧室
  legendList.push({
    staticMessageKey: 'm_static_message-label.restroom',
    imageUrl: 'assets/images/seatlayout_lavatory_29.png',
  });
  // 化粧室 車いす可
  legendList.push({
    staticMessageKey: 'm_static_message-label.restroomWheelchairAvailable',
    imageUrl: 'assets/images/seatlayout_lavatory_wheelchair_29.png',
  });
  // 化粧室 おむつ交換台付き
  legendList.push({
    staticMessageKey: 'm_static_message-label.restroomWithDiaperStand',
    imageUrl: 'assets/images/seatlayout_lavatory_infant_29.png',
  });
  // 温水洗浄機能付き化粧室
  legendList.push({
    staticMessageKey: 'm_static_message-label.restroomWithMultifunctionBidet',
    imageUrl: 'assets/images/seatlayout_lavatory_hotwater_29.png',
  });
  // 多目的ルーム
  legendList.push({
    staticMessageKey: 'm_static_message-label.multipurposeRoom',
    imageUrl: 'assets/images/seatlayout_multi_room_29.png',
  });
  // 非常口座席
  legendList.push({
    staticMessageKey: 'm_static_message-label.exitSeat',
    imageUrl: 'assets/images/seatlayout_emergency_exit_seat_29.png',
  });
  // 窓なし席
  legendList.push({
    staticMessageKey: 'm_static_message-label.noWindowSeat',
    imageUrl: 'assets/images/seatlayout_no_window_seat_29.png',
  });
  // リクライニングしない席
  legendList.push({
    staticMessageKey: 'm_static_message-label.impossibleReclining',
    imageUrl: 'assets/images/seatlayout_no_reclining_seat_29.png',
  });
  // アームレストが上がらない席
  legendList.push({
    staticMessageKey: 'm_static_message-label.armrestNotMovableSeat',
    imageUrl: 'assets/images/seatlayout_no_uparm_seat_29.png',
  });
  // テーブル
  legendList.push({
    staticMessageKey: 'm_static_message-label.table',
    imageUrl: 'assets/images/seatlayout_table_29.png',
  });
  // 可動式パーテション
  legendList.push({
    staticMessageKey: 'm_static_message-label.movablePartition',
    imageUrl: 'assets/images/seatlayout_partition_29.png',
  });
  // 階段
  legendList.push({
    staticMessageKey: 'm_static_message-label.stairs',
    imageUrl: 'assets/images/seatlayout_stairs_29.png',
  });
  return legendList;
}

/**
 * カウチシート座席番号を、行番号と列番号部分に分解する
 *
 * 例）'39HJK' → { rowNumber: '39', columnNumbers: ['H', 'J', 'K'] }
 * @param key
 * @returns
 */
export function splitCouchSeatNumber(key?: string) {
  // 先頭文字の連続した数字を１つの値として抜き出し
  const regexNumber = /^[0-9]+/g;
  const rowNumbers = key?.match(regexNumber);

  // アルファベットを１文字ずつ別の値として抜き出し
  const regexAlphabet = /[A-Z]/g;
  const columnNumbers = key?.match(regexAlphabet);
  return { rowNumber: rowNumbers?.[0], columnNumbers: columnNumbers };
}

/**
 * 配列を指定した条件にて分割し二次元配列にした [シャローコピー](https://developer.mozilla.org/ja/docs/Glossary/Shallow_copy) を作成する。
 *
 * 下記は使用例。
 *
 * ```typescript
 * const originArray = [{ text: 'a' }, { text: 'b' }, { text: 'c' }, { text: 'd' }];
 * const splitted = splitArray(originArray, (value) => value.text === 'c');
 * console.log(splitted)
 * => [[{"text": "a"}, {"text": "b"}], [{"text": "d"}]]
 * ```
 *
 * @param originArray 元となる配列
 * @param separatorFunc 区切りとなる要素を指定するための条件
 * @returns 分割後の二次元配列
 */
export function splitArray<T>(originArray: T[], separatorFunc: (originArrayValue: T) => boolean) {
  const lastIndex = originArray.length - 1;
  const results: T[][] = [];
  let tmp: T[] = [];
  originArray.forEach((value, index) => {
    if (separatorFunc(value)) {
      results.push(tmp);
      tmp = new Array();
    } else {
      tmp.push(value);
      if (lastIndex === index) {
        results.push(tmp);
      }
    }
  });
  return results;
}

/**
 * 画面表示サイズに応じたボタンにフォーカスを設定する
 * @param ids 画面サイズごとのID
 */
export function focusOnCurrentButton(renderer: Renderer2, ids: OpenModalButtonIds) {
  const getId = () => {
    if (isPC()) {
      return ids.idForPC;
    } else if (isTB()) {
      return ids.idForTab;
    } else {
      return ids.idForSp;
    }
  };
  const id = getId();
  if (!!id) {
    const openModalButton = renderer.selectRootElement(`#${id}`, true);
    openModalButton.focus();
  }
}
